import { useState } from 'react';
import { Button } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IUsePersistentFormResult } from '~/components/utils/PersistentForm';
import type { IFormMode } from '~/helpers/types';
import type {
  IArticleFormSubmitAction,
  IArticleFormValues,
} from './ArticleForm';
import { NotifyArticleDialog } from './NotifyArticleDialog';

export type IArticleFormActionBarProps = {
  mode: IFormMode;
  form: IUsePersistentFormResult<IArticleFormValues>;
  onSubmit?: (
    values: IArticleFormValues,
    event?: React.BaseSyntheticEvent,
    action?: IArticleFormSubmitAction,
  ) => void;
  submittingAction?: IArticleFormSubmitAction;
  published?: boolean;
  publicationScheduled?: boolean;
};

const styles = stylex.create({
  dialog: {
    minWidth: 560,
  },
});

export const ArticleFormSubmitButtons: React.FC<IArticleFormActionBarProps> = ({
  mode,
  form,
  onSubmit,
  submittingAction,
  published,
  publicationScheduled,
}) => {
  const { t } = useTranslation();
  const [publishOptionsModalOpened, setPublishOptionsModalOpened] =
    useState(false);

  return (
    <>
      <NotifyArticleDialog
        mode={mode}
        sx={styles.dialog}
        opened={publishOptionsModalOpened}
        form={form}
        onClose={() => setPublishOptionsModalOpened(false)}
        onSubmit={onSubmit}
      />

      {published ? (
        <>
          <Button
            type="submit"
            loading={submittingAction === 'publish'}
            onClick={
              onSubmit
                ? form.handleSubmit((values, event) =>
                    onSubmit(values, event, 'publish'),
                  )
                : undefined
            }
            disabled={!!submittingAction}
          >
            {t('common.update')}
          </Button>
          <Button
            variant="filledTonal"
            loading={submittingAction === 'publishAndNotify'}
            onClick={() => {
              form.setValue('notificationTitle', form.watch('title'));
              setPublishOptionsModalOpened(true);
            }}
            disabled={!!submittingAction}
          >
            {t('ArticleFormSubmitButtons.update-and-notify')}
          </Button>
          <Button
            variant="filledTonal"
            loading={submittingAction === 'unpublish'}
            onClick={
              onSubmit
                ? form.handleSubmit((values, event) =>
                    onSubmit(values, event, 'unpublish'),
                  )
                : undefined
            }
            disabled={!!submittingAction}
          >
            {t('ArticleFormSubmitButtons.unpublish-now')}
          </Button>
        </>
      ) : (
        <>
          <Button
            type="submit"
            loading={submittingAction === 'publish'}
            onClick={
              onSubmit
                ? form.handleSubmit((values, event) =>
                    onSubmit(values, event, 'publish'),
                  )
                : undefined
            }
            disabled={!!submittingAction}
          >
            {publicationScheduled
              ? t('ArticleFormSubmitButtons.schedule')
              : t('ArticleFormSubmitButtons.publish-now')}
          </Button>
          <Button
            variant="filledTonal"
            loading={submittingAction === 'publishAndNotify'}
            onClick={() => {
              form.setValue('notificationTitle', form.watch('title'));
              setPublishOptionsModalOpened(true);
            }}
            disabled={!!submittingAction}
          >
            {publicationScheduled
              ? t('ArticleFormSubmitButtons.schedule')
              : t('ArticleFormSubmitButtons.publish-now')}{' '}
            {t('ArticleFormSubmitButtons.and-notify')}
          </Button>
          <Button
            variant="filledTonal"
            loading={submittingAction === 'saveDraft'}
            onClick={() => {
              form.setValue('publicationScheduled', false);
              form.setValue('unpublicationScheduled', false);

              if (onSubmit) {
                void form.handleSubmit((values, event) =>
                  onSubmit(values, event, 'saveDraft'),
                )();
              }
            }}
            disabled={!!submittingAction}
          >
            {t('ArticleFormSubmitButtons.save-draft')}
          </Button>
        </>
      )}
    </>
  );
};
